import { Dispatch, SetStateAction, useState } from 'react'

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Box, Button, Grid, TextField, Typography } from '@mui/material'
import { GAME_TYPE, GameType } from 'constants/enums/GameTypeEnum'
import { brainstormText } from 'content/brainstormText'
import { EMPTY_ARRAY_CHECK } from 'utils/ideationRegex'

import AgreedTermsCheckbox from 'components/AgreedTermsCheckbox/AgreedTermsCheckbox'
import FacilitationHeader from 'components/FacilitationHeader'
import { RichTextField } from 'components/Form'
import IdeaFlameMotivator, {
	useRichTextToIdeas,
} from 'components/IdeaFlameMotivator/IdeaFlameMotivator'
import PromptBox from 'components/PromptBoxV2'

import { DisruptionType } from '../../types'
import { styles } from './IdeationSection.styles'

export const TEST_ID = 'battle-ships-ideation-section'

export type Props = {
	activeType: DisruptionType
	authorIdeation: string[]
	cautionText?: string
	competitorResponses: string[]
	gameId?: string
	gameTypeEnum: GameType
	generateCompetitorResponse: () => Promise<void>
	handleChange: (i: number) => (val: string) => void
	handleSubmitIdeas: () => void
	isLoading: boolean
	setCompetitorResponse: (text: string) => void
	setTermsAgreed: Dispatch<SetStateAction<boolean>>
	submitIdeasDisabled: () => boolean
	termsAgreed: boolean
	viewSubmittedIdeas: () => void
}

const IdeationSection = ({
	activeType,
	authorIdeation,
	isLoading,
	handleChange,
	submitIdeasDisabled,
	handleSubmitIdeas,
	viewSubmittedIdeas,
	cautionText,
	setTermsAgreed,
	termsAgreed,
	gameId,
	generateCompetitorResponse,
	competitorResponses,
	setCompetitorResponse,
}: Props) => {
	const richTextToIdeasFns = [useRichTextToIdeas(), useRichTextToIdeas()]
	const [isEditMode, setIsEditMode] = useState(false)
	const [editedText, setEditedText] = useState('')
	const showCompetitorResponse = competitorResponses.length > 0 || isLoading

	const handleSaveButtonClick = () => {
		setCompetitorResponse(editedText)
		setIsEditMode(false)
	}

	const onClickCancelEdit = () => {
		setIsEditMode(false)
	}

	return (
		<>
			<Typography sx={styles.ideationTypography}>3. IDEATION</Typography>
			<Grid sx={styles.mainBox}>
				<Box sx={styles.sideBar} />
				<Box sx={styles.typographyBox}>
					<Typography
						sx={styles.firstRichTextFieldDescription}
						data-testid={`${TEST_ID}__first_typography`}
					>
						{brainstormText.battleShips.ideationTitles[0]}
					</Typography>
					<Grid
						item
						sx={styles.richTextFieldGrid}
						data-testid={`${TEST_ID}__ideation`}
					>
						<RichTextField
							onChange={handleChange(0)}
							placeholderText={
								brainstormText.battleShips
									.ideationPlaceholders[0]
							}
							value={authorIdeation[0] ?? ''}
							sx={styles.richTextField}
							iconColor="#45657B"
							key={`editor-0-${activeType}`}
							editorId={`editor-0-${activeType}`}
						/>
					</Grid>
					<Box>
						<IdeaFlameMotivator
							ideaCount={richTextToIdeasFns[0](authorIdeation[0])}
							variant="BATTLE_SHIPS"
						/>
					</Box>
					<Typography
						sx={styles.richTextFieldDescription}
						data-testid={`${TEST_ID}__first_typography`}
					>
						{brainstormText.battleShips.ideationTitles[1]}
					</Typography>
					{!showCompetitorResponse && (
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__generate-comp-response-button`}
							disabled={
								!authorIdeation[0]
									?.replace(EMPTY_ARRAY_CHECK, '')
									.trim().length || isLoading
							}
							sx={styles.generateButton}
							onClick={() => generateCompetitorResponse()}
							startIcon={<AutoAwesomeIcon />}
						>
							<Typography>
								Generate AI competitor response
							</Typography>
						</Button>
					)}
					{showCompetitorResponse && (
						<>
							<FacilitationHeader
								onRefresh={() => generateCompetitorResponse()}
								onEdit={
									isEditMode
										? undefined
										: () => {
												setEditedText(
													competitorResponses.join(
														'\n'
													)
												)
												setIsEditMode(true)
											}
								}
								isLoading={isLoading}
								refreshTestId={`${TEST_ID}-prompt-section__prompt-box_main-refresh`}
								editTestId={`${TEST_ID}-prompt-section-edit-prompt`}
							/>
							{isEditMode ? (
								<>
									<TextField
										data-testid={`${TEST_ID}-prompt-section__edit-company-disruption`}
										value={editedText}
										onChange={(e) =>
											setEditedText(e.target.value)
										}
										multiline
										fullWidth
										inputRef={(input) =>
											input && input.focus()
										}
									/>
									<Box sx={styles.editButtonsContainer}>
										<Button
											color="secondary"
											variant="contained"
											data-testid={`${TEST_ID}-prompt-section__edit-save-button`}
											onClick={() =>
												handleSaveButtonClick()
											}
										>
											<Typography>
												SAVE CHANGES
											</Typography>
										</Button>
										<Button
											variant="text"
											color="secondary"
											data-testid={`${TEST_ID}-prompt-section__edit-cancel-button`}
											onClick={onClickCancelEdit}
										>
											<Typography>CANCEL</Typography>
										</Button>
									</Box>
								</>
							) : (
								<PromptBox
									gameTypeEnum={GAME_TYPE.BATTLE_SHIPS}
									contentText={
										competitorResponses.length > 0
											? competitorResponses.join('\n')
											: 'Generating competitor response...'
									}
									data-testid="company-disruption"
									loading={isLoading}
									hidePromptContainerStyle={true}
								/>
							)}
						</>
					)}
					<Typography
						sx={styles.richTextFieldDescription}
						data-testid={`${TEST_ID}__first_typography`}
					>
						{brainstormText.battleShips.ideationTitles[2]}
					</Typography>
					<Grid
						item
						sx={styles.richTextFieldGrid}
						data-testid={`${TEST_ID}__ideation`}
					>
						<RichTextField
							onChange={handleChange(1)}
							placeholderText={
								brainstormText.battleShips
									.ideationPlaceholders[1]
							}
							value={authorIdeation[1] ?? ''}
							sx={styles.richTextField}
							iconColor="#45657B"
							key={`editor-1-${activeType}`}
							editorId={`editor-1-${activeType}`}
							disabled={competitorResponses.length === 0}
						/>
					</Grid>
					<Box>
						<IdeaFlameMotivator
							ideaCount={richTextToIdeasFns[1](authorIdeation[1])}
							variant="BATTLE_SHIPS"
						/>
					</Box>
					<AgreedTermsCheckbox
						setTermsAgreed={setTermsAgreed}
						termsAgreed={termsAgreed}
						gameId={gameId}
					/>
					<Box sx={styles.submitButtonBox}>
						<Button
							color="secondary"
							variant="contained"
							data-testid={`${TEST_ID}__submit-idea-button`}
							onClick={() => {
								handleSubmitIdeas()
							}}
							disabled={submitIdeasDisabled() || isLoading}
						>
							<Typography>SUBMIT IDEAS</Typography>
						</Button>
						<Button
							disabled={isLoading}
							variant="text"
							color="secondary"
							data-testid={`${TEST_ID}__view-submit-ideas-button`}
							onClick={viewSubmittedIdeas}
						>
							<Typography>VIEW ALL SUBMITTED IDEAS</Typography>
						</Button>
					</Box>
					<Typography sx={styles.cautionTypography}>
						{cautionText}
					</Typography>
				</Box>
			</Grid>
		</>
	)
}

export default IdeationSection
